import { useEffect, useMemo, useState } from 'react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

interface Props {
  aberto: boolean;
  projetoId?: string;
  destinoId: string;
  onFechar: () => void;
  onSucesso: () => void;
}

interface ApreciacaoItem {
  id: string;
  numero: string;
  titulo: string;
  statusLiberacao?: string;
  nomeMaquina?: string;
}

export function CopyFromApreciacaoModal({ aberto, projetoId, destinoId, onFechar, onSucesso }: Props) {
  const { t } = useI18n();
  const [lista, setLista] = useState<ApreciacaoItem[]>([]);
  const [busca, setBusca] = useState('');
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [copiando, setCopiando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    setCarregando(true);
    setErro(null);
    apiService
      .listarApreciacoes({ projetoId, limit: 200 })
      .then((response) => {
        if (cancelado) return;
        if (response.success && response.data) {
          const itens = Array.isArray(response.data)
            ? response.data
            : ((response.data as any)?.apreciacoes || (response.data as any)?.items || []);
          setLista(itens.filter((a: ApreciacaoItem) => a.id !== destinoId));
        } else {
          setErro(response.message || t('apreciacoes.editar.copiar.erroLista'));
        }
      })
      .catch((e) => { if (!cancelado) setErro(e?.message || t('apreciacoes.editar.copiar.erroCarregar')); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [aberto, projetoId, destinoId, t]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return lista;
    return lista.filter(
      (a) =>
        (a.titulo || '').toLowerCase().includes(q) ||
        (a.numero || '').toLowerCase().includes(q) ||
        (a.nomeMaquina || '').toLowerCase().includes(q)
    );
  }, [lista, busca]);

  const confirmar = async () => {
    if (!selecionadaId) return;
    setCopiando(true);
    setErro(null);
    try {
      const response = await apiService.copiarCabecalhoDeApreciacao(destinoId, selecionadaId);
      if (!response.success) {
        setErro(response.message || t('apreciacoes.editar.copiar.falha'));
        return;
      }
      onSucesso();
      onFechar();
    } catch (e: any) {
      setErro(e?.message || t('apreciacoes.editar.copiar.erro'));
    } finally {
      setCopiando(false);
    }
  };

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center">
      <div className="w-full max-w-3xl bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl border border-schmersal-gray-light dark:border-schmersal-gray-dark max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
          <h3 className="text-lg font-bold text-schmersal-blue dark:text-white">
            {t('apreciacoes.editar.copiar.titulo')}
          </h3>
          <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            {t('apreciacoes.editar.copiar.subtitulo')}
          </p>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder={t('apreciacoes.editar.copiar.buscaPlaceholder')}
            className="w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg px-3 py-2
                       bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white"
          />

          {carregando && <p className="text-sm text-schmersal-gray-medium">{t('apreciacoes.editar.copiar.carregando')}</p>}
          {erro && <p className="text-sm text-red-600">{erro}</p>}

          <div className="border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg divide-y divide-rg-hairline dark:divide-schmersal-gray-dark max-h-96 overflow-y-auto">
            {filtradas.length === 0 && !carregando && (
              <p className="text-sm text-schmersal-gray-medium p-4">{t('apreciacoes.editar.copiar.vazio')}</p>
            )}
            {filtradas.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setSelecionadaId(a.id)}
                className={`w-full text-left p-3 hover:bg-schmersal-gray-lightest dark:hover:bg-schmersal-gray-dark ${
                  selecionadaId === a.id ? 'bg-blue-50 dark:bg-schmersal-gray-dark' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-schmersal-gray-dark dark:text-white">
                      {a.numero ? `${a.numero} — ` : ''}{a.titulo || t('apreciacoes.editar.copiar.semTitulo')}
                    </div>
                    {a.nomeMaquina && (
                      <div className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {t('apreciacoes.editar.copiar.maquina', { nome: a.nomeMaquina })}
                      </div>
                    )}
                  </div>
                  {a.statusLiberacao && (
                    <span className="text-xs px-2 py-1 rounded bg-schmersal-gray-light dark:bg-schmersal-gray-dark">
                      {a.statusLiberacao}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onFechar}
            disabled={copiando}
            className="px-4 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                       text-schmersal-gray-dark dark:text-white disabled:opacity-60"
          >
            {t('comum.acao.cancelar')}
          </button>
          <button
            type="button"
            onClick={confirmar}
            disabled={!selecionadaId || copiando}
            className="px-4 py-2 rounded-lg bg-schmersal-blue text-white font-semibold disabled:opacity-60"
          >
            {copiando ? t('apreciacoes.editar.copiar.copiando') : t('apreciacoes.editar.copiar.confirmar')}
          </button>
        </div>
      </div>
    </div>
  );
}
